import { supabase, SUPABASE_URL } from './supabase';

export interface Recipe {
  id: string;
  title: string;
  description?: string;
  image: string;
  images: string[];
  category: string;
  calories: number;
  rating: number;
  reviewCount: number;
  prepTime: number;
  cookTime: number;
  servings?: number;
  author?: string;
  cookbook?: string;
  ingredients: string[];
  directions: string[];
  created_at: string;
}

export const CATEGORIES = ['All', 'Breakfast', 'Lunch', 'Dinner', 'Snack', 'Dessert'];

const FETCH_PAGE_SIZE = 1000;

export async function fetchRecipes(): Promise<Recipe[]> {
  // Supabase caps each request at 1000 rows, so page through until we've got them all
  const all: any[] = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from('recipes')
      .select('*')
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .range(from, from + FETCH_PAGE_SIZE - 1);

    if (error) {
      console.error('Error fetching recipes:', error);
      return all.map(mapRecipe);
    }

    if (data && data.length > 0) {
      all.push(...data);
    }

    if (!data || data.length < FETCH_PAGE_SIZE) break;
    from += FETCH_PAGE_SIZE;
  }

  // Guard against duplicates if rows shifted between page requests
  const seen = new Set<string>();
  const unique = all.filter((r) => {
    if (seen.has(r.id)) return false;
    seen.add(r.id);
    return true;
  });

  return unique.map(mapRecipe);
}

export async function fetchRecipesByCategory(category: string): Promise<Recipe[]> {
  if (category === 'All') {
    return fetchRecipes();
  }

  const { data, error } = await supabase
    .from('recipes')
    .select('*')
    .eq('meal', category)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching recipes by category:', error);
    return [];
  }

  return (data || []).map(mapRecipe);
}

export async function fetchRecipeById(id: string): Promise<Recipe | null> {
  const { data, error } = await supabase
    .from('recipes')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching recipe:', error);
    return null;
  }

  return data ? mapRecipe(data) : null;
}

export async function fetchSpotlightRecipes(): Promise<Recipe[]> {
  const { data, error } = await supabase
    .from('recipes')
    .select('*')
    .gte('review_rating', 4.2)
    .order('review_rating', { ascending: false, nullsFirst: false })
    .limit(40);

  if (error) {
    console.error('Error fetching spotlight recipes:', error);
    return [];
  }

  const recipes = (data || []).map(mapRecipe);
  
  // Shuffle the recipes randomly
  for (let i = recipes.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [recipes[i], recipes[j]] = [recipes[j], recipes[i]];
  }
  
  return recipes;
}

export async function searchRecipes(query: string): Promise<Recipe[]> {
  const { data, error } = await supabase
    .from('recipes')
    .select('*')
    .ilike('title', `%${query}%`)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error searching recipes:', error);
    return [];
  }

  return (data || []).map(mapRecipe);
}

const STORAGE_BUCKET = 'recipe-images';

function buildImageUrl(recipeId: string, quality: number = 50): string {
  if (!recipeId || !SUPABASE_URL) return '';
  
  // Pattern: recipes:{recipe-id}:main.png
  const imagePath = `recipes:${recipeId}:main.png`;
  return `${SUPABASE_URL}/storage/v1/object/public/${STORAGE_BUCKET}/${imagePath}?quality=${quality}`;
}

function normalizeCategory(meal: string | null): string {
  if (!meal) return 'Other';
  const trimmed = meal.trim();
  const capitalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  return CATEGORIES.includes(capitalized) ? capitalized : 'Other';
}

function mapRecipe(data: any): Recipe {
  const imageUrl = buildImageUrl(data.id);
  
  return {
    id: data.id,
    title: data.title || 'Untitled Recipe',
    description: data.description || '',
    image: imageUrl || '/placeholder-recipe.png',
    images: imageUrl ? [imageUrl] : [],
    category: normalizeCategory(data.meal),
    calories: data.calories || 0,
    rating: parseFloat(data.review_rating) || 0,
    reviewCount: data.review_quantity || 0,
    prepTime: data.prep_time || 0,
    cookTime: data.cook_time || 0,
    servings: data.servings || 4,
    author: data.author || '',
    cookbook: data.cookbook || '',
    ingredients: data.ingredients || [],
    directions: data.directions || [],
    created_at: data.created_at,
  };
}
